import React, { useEffect, useState, useRef } from "react";
import { trackifyPageChange } from "@allianz/tracking";

//** MUI Components */
import { Box, Fade, Skeleton, Typography, Checkbox } from "@mui/material";
import { useNavigate, useOutletContext } from "react-router-dom";

//** NDBX */
import { Button, ButtonStack, OneColumnStack, Section } from "@arp/react-ndbx";

//** Context  */
import useNewBusiness from "../../hooks/useNewBusiness.ts";
import usePreQualification from "../../hooks/usePreQualification.ts";
import usePreQualificationPages from "../../hooks/usePreQualificationPages.ts";
import useSharedData from "../../hooks/useSharedData.ts";

//** Components */
import Page from "../../components/Page";
import AccordionTables from "../../components/pageLevel/quoteSummary/AccordionTables.jsx";

//** Hooks */
import { useGetInterviewOutcome } from "../../hooks/dataHooks/useGetInterviewOutcome.js";
import { useGetPremiumProjectionData } from "../../hooks/dataHooks/useGetPremiumProjectionData.js";
import useAuth from "../../hooks/useAuth.ts";

//** Config  */
import {
  AURA_OUTCOME,
  Application,
  TRACKIFY_EVENT,
  sendEmailModalContent,
} from "../../config/const/enums.js";
import { pageOrderCC } from "../../config/pageInfo/pageOrder";
import { retrieveQuoteNotification } from "../../config/disclaimers/disclaimers.jsx";
import { policyOutcomeDisclaimers } from "../../config/disclaimers/disclaimers.jsx";

import FadeInOut from "../../utilities/fadeInOut.jsx";
import { isObjEmpty } from "../../utilities/utils.js";
import { getTokenClaim } from "../../utilities/tokenUtil.js";

// import {
// 	formatCurrency,
// 	formatCurrencyNoDecimal,
// } from "../../utilities/numericFormat.js";

//** Page components  */
import NotificationCard from "../../components/coding/notificationCard/NotificationCard.jsx";
import OutcomeTermsAndConditions from "../../components/pageLevel/policyOutcome/OutcomeTermsAndConditions.jsx";
import SuccessfulHeadlineHeader from "../../components/pageLevel/policyOutcome/SuccessfulHeadlineHeader.jsx";
import SuccessfulOutcomeDetail from "../../components/pageLevel/policyOutcome/SuccessfulOutcomeDetail.jsx";
import UpgradePolicy from "../../components/pageLevel/policyOutcome/UpgradePolicy.jsx";
import SendEmailModal from "../../components/pageLevel/sendEmailModal/SendEmailModal.jsx";
import { PATH_CONTACT_CENTER } from "../../routes/paths.js";
import { ConstructionRounded } from "@mui/icons-material";

// ----------------------------------------------------------------

const SuccessfulOutcome = (props) => {
  const {
    fadeIn,
    handleOnChange,
    currentPage,
    frequency,
    dataSet,
    isDirty,
    isValid,
    retrieveQuoteJourney,
    isOnline = false,
    accessToken,
    isRetrievePolicyExpired = false,
    errors,
    setValue,
    handleSubmit,
    handleInputChange,
    policyOutcomeAcceptTerms,
  } = {
    ...props,
  };

  /* #region Send Email */
  const [openSendEmailModal, setOpenSendEmailModal] = useState(false);
  const [nextEnabled, setNextEnabled] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const handleSendEmailOpen = () => {
    trackifyPageChange(TRACKIFY_EVENT.CC_SEND_EMAIL_POLICY_OUTCOME);
    setOpenSendEmailModal(true);
  };
  const handleSendEmailClose = (event, reason) => {
    if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
      setOpenSendEmailModal(false);
      setSendQuoteEmailSuccess(false);
    }
  };
  const [sendQuoteEmailSuccess, setSendQuoteEmailSuccess] = useState(false);
  const handleSuccessEmailModal = () => {
    setSendQuoteEmailSuccess(true);
  };
  /* #endregion */

  const { getOktaAccessToken } = useAuth();
  const navigate = useNavigate();

  //* If there are any exclusions across any of the cover, we need to
  //* show the "special" T&Cs
  //* However, if we remove a cover, with exclusions and there
  //* is no other cover with exclusions, we have to show the
  //* default T&Cs.
  // const [showAdditionalExclusionsTerms, setShowAdditionalExclusionsTerms] =
  // 	useState(false);

  let showAdditionalExclusionsTerms = false;
  const onHandleAdditionConditions = (boolVal) => {
    // setShowAdditionalExclusionsTerms(boolVal);
    showAdditionalExclusionsTerms = !showAdditionalExclusionsTerms;
  };

  //* Shared data
  const { handleAdditionalConditionsChange } = useSharedData();

  //* New Business data
  const { data: newBusinessState, isDataUpdating } = useNewBusiness();
  let showExclusionsTerms = newBusinessState?.covers?.anyExclusionsOrLoadings;

  const handleCoverCheckboxCheck = (boolVal) => {
    //** Update the acceptTerms checkbox */
    handleInputChange({
      name: "policyOutcomeAcceptTerms",
      value: policyOutcomeAcceptTerms,
    });
  };

  //! TODO: PUT BACK IN 12/04/2024
  //* Get Premium Projections - ONLY in CC flow
  const { data: premiumProjectionData, refetch } = useGetPremiumProjectionData(
    accessToken,
    !isRetrievePolicyExpired
  );

  // const premiumProjectionData = null;
  //! TODO:  PUT BACK IN 12/04/2024

  //* Need to check if the user has Under Writer privileges
  const [isUWUserAccess, setIsUWUserAccess] = useState(false); //! TODO: SET TO FALSE 12/04/2024
  useEffect(() => {
    trackifyPageChange(currentPage?.identifier?.toLowerCase());
    setOutSideChecked(dataSet?.policyOutcomeAcceptTerms);

    //* We need to check the Okta token and make sure that the
    //* current user is a CC user (they must be if they get this far)
    //* and that check if they have Underwriting access/permissions
    //* For testing, the Okta token can be found in the call to the
    //* API callCentreCreateQuote
    const checkIsUWUser = async () => {
      const isDev = false;
      if (isDev === false) {
        let oktaAccessTokenForCC = await getOktaAccessToken();
        const { accessToken } = { ...oktaAccessTokenForCC };
        const oktaAccessToken =
          isDev === false
            ? accessToken
            : "eyJhbGciOiJIUzI1NiJ9.eyJhdWQiOiJodHRwczovL2Rldi5lbGlmZWluc3VyZS5jb20uYXUvb2F1dGgyL3Rva2VuIiwiaXNzIjoidGVybWxpZmUtYXBpLmRldiIsInN1YiI6InRlcm1saWZlLWFwaS5kZXYuand0IiwiaWF0IjoxNzEzMzA1MTQ1LCJleHAiOjE3MTMzMzM5NDUsImp0aSI6Ijg3M2I1MWVhLTRmMTItNGExNC05NDk3LWM4MTJlMWQxNDE2YyIsInFpZCI6IjYzNFE3MDA0MjMyQUxQIiwiZWx0aWQiOiJjNTFkOWVjMi1lNGY4LTQ2NjEtOGY1My0yMTM5OTQ3NWM3MzQiLCJva3RhVXNlcklkIjoiMDB1NDFudjgwNHNBSERSTTYzbDciLCJjY191c2VyIjp0cnVlLCJ1c2VybmFtZSI6ImpjcWIiLCJmaXJzdE5hbWUiOiJDb2xpbiIsImxhc3ROYW1lIjoiQnlybmUifQ.t5HVuxx10lB6270wNuJj0_9eu-i80UsWSUWW1nd0_rk";

        setIsUWUserAccess(
          getTokenClaim({ accessToken: oktaAccessToken, claim: "isUW" })
        );
      }
    };
    checkIsUWUser();
  }, []);

  //* Additional Information interaction
  const [outSideChecked, setOutSideChecked] = useState(
    dataSet?.policyOutcomeAcceptTerms
  );

  useEffect(() => {
    if (isUWUserAccess === false || handleAdditionalConditionsChange === false)
      return;
    setOutSideChecked(true);
  }, [handleAdditionalConditionsChange]);

  const handleCheckboxSet = (val) => {
    if (isUWUserAccess === false) return;
    if (val === true) {
      setOutSideChecked(false);
    }
  };
  //* Additional Information interaction

  return (
    <>
      <Fade in={fadeIn} timeout={500}>
        <Box>
          <SuccessfulHeadlineHeader onChange={(e) => handleOnChange(e)} />
        </Box>
      </Fade>
      <Page title={currentPage.title} identifier={currentPage.identifier}>
        {retrieveQuoteJourney && (
          <Section md={7} mb={4}>
            <FadeInOut show={1 === 1} duration={100} style={{ width: "100%" }}>
              <Box>
                <NotificationCard
                  cardType="info"
                  title="Application retrieval important information"
                  message={policyOutcomeDisclaimers({
                    isOnline: false,
                  }).policyOutcomeInfo[0].POLICY_OUTCOME_RETRIEVAL(
                    dataSet["personOne"].emailAddress,
                    dataSet["personOne"].contactNumber
                  )}
                />
              </Box>
            </FadeInOut>
          </Section>
        )}
        {retrieveQuoteJourney && isRetrievePolicyExpired && (
          <Section md={7} mb={4}>
            <FadeInOut show={1 === 1} duration={100} style={{ width: "100%" }}>
              <Box>
                <NotificationCard
                  cardType="info"
                  title="Policy Expired"
                  message={retrieveQuoteNotification().POLICY_EXPIRED}
                />
              </Box>
            </FadeInOut>
          </Section>
        )}
        <Fade in={fadeIn} timeout={500}>
          <Box>
            <SuccessfulOutcomeDetail
              person="personOne"
              frequency={frequency}
              application={Application.CONTACT_CENTER}
              dataSet={dataSet}
              onCheckedChange={(val) => handleCoverCheckboxCheck(val)}
              isRetrievePolicyExpired={isRetrievePolicyExpired}
              retrieveQuoteJourney={retrieveQuoteJourney}
              isUWUser={isUWUserAccess}
              onAdditionConditions={(val) => onHandleAdditionConditions(val)}
            />
            {showExclusionsTerms === true ? (
              <>
                <OutcomeTermsAndConditions
                  showSpecialTerms={showExclusionsTerms}
                  onLine={false}
                  dataSet={dataSet}
                  resetCheckbox={true}
                  setValue={setValue}
                  handleSubmit={handleSubmit}
                  reset={outSideChecked}
                  parentOnChange={(val) => {
                    handleCheckboxSet(val);
                  }}
                  isUW={isUWUserAccess}
                />
              </>
            ) : null}
            <OneColumnStack maxWidth="670px" mt={4}>
              <Section mb={2}>
                <Typography variant="h5-bold">Allianz Life Premiums</Typography>
              </Section>
              <NotificationCard
                cardType="info"
                title=""
                alignIcon="flex-start"
                message={
                  policyOutcomeDisclaimers({
                    isOnline: false,
                  }).policyOutcomeInfo[0].OUTCOME_PREMIUM_PROJECTION.note
                }
              />
            </OneColumnStack>
            {premiumProjectionData ? (
              <Section mt={4}>
                <AccordionTables
                  frequency={frequency}
                  person="personOne"
                  dataSet={dataSet}
                  showSecond={false}
                  premiumData={premiumProjectionData}
                  justifyContent={"space-evenly"}
                  stackMargin={{ xs: 2, sm: 0, md: -1 }}
                  parentPage="policyOutcomePage"
                />
              </Section>
            ) : null}
            <OneColumnStack maxWidth="690px" mt={4}>
              <UpgradePolicy
                dataSet={dataSet}
                setUpgradeChecked={setShowNext}
                setUpgradeSuccess={setNextEnabled}
                accessToken={accessToken}
              />
            </OneColumnStack>
          </Box>
        </Fade>
        <Section mt={8}>
          <ButtonStack>
            <Button
              variant="contained"
              color="secondary"
              disabled={!isDirty || !isValid || isRetrievePolicyExpired}
              onClick={handleSendEmailOpen}
            >
              SAVE AND EMAIL
            </Button>
            {showNext ? (
              <Button
                variant="contained"
                color="secondary"
                disabled={!nextEnabled}
                onClick={() => {
                  navigate(PATH_CONTACT_CENTER.upgradeSaveConfirmation);
                }}
              >
                NEXT
              </Button>
            ) : (
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={!isDirty || !isValid || isRetrievePolicyExpired}
              >
                PURCHASE POLICY
              </Button>
            )}
          </ButtonStack>
        </Section>
        <SendEmailModal
          openSendEmailModal={openSendEmailModal}
          handleSendEmailClose={handleSendEmailClose}
          handleSuccessEmailModal={handleSuccessEmailModal}
          sendQuoteEmailSuccess={sendQuoteEmailSuccess}
          sendEmailImg={sendEmailModalContent.SEND_EMAIL_IMAGE}
        />
      </Page>
    </>
  );
};

//----------------------------------------------------------------

export default function PolicyOutcomeCC() {
  const { state, funcs } = usePreQualificationPages();
  const { currentPage } = { ...state };

  const decline = () => {
    funcs.validateAndProceed(pageOrderCC.PP_POLICY_OUTCOME_DECLINE, true);
  };

  const {
    methods: [methods],
    showSecond: [showSecond],
    isDirty: [isDirty],
    isValid: [isValid],
    errors: [errors],
  } = useOutletContext();

  const { trigger, setValue, handleSubmit } = { ...methods };

  //* PreQual state data
  const { updateStateOnRetrieval, dataSet, handleInputChange } =
    usePreQualification();

  //* Shared data
  const {
    accessToken,
    retrieveQuoteJourney,
    isRetrievePolicyExpired,
    isOutcomeDataLoaded,
    handleEventChange,
  } = useSharedData();
  const [policyOutcomeData, setPolicyOutcomeData] = useState({});

  //* New Business data
  const {
    data: newBusinessState,
    setInitialData,
    isDataUpdating,
  } = useNewBusiness();

  //* Interview question outcome from Aura
  //* Note: isInitialLoading is derived from isLoading && isFetching
  const {
    isLoading,
    isFetching,
    isInitialLoading,
    data: outcomeData,
    error,
    refetch,
  } = useGetInterviewOutcome({
    accessToken: accessToken,
    enableGet: !retrieveQuoteJourney, //* Don't call on a retrieved policy.
  });

  useEffect(() => {
    const updateLocalState = async () => {
      if (
        isInitialLoading ||
        outcomeData == null ||
        retrieveQuoteJourney === true
      )
        return;
      // if (outcomeData.fromOriginal === false) {
      if (isOutcomeDataLoaded === false) {
        await updateStateOnRetrieval({
          data: outcomeData,
          updateType: "policyInfo",
        });
        //* We now need to put the outcome data in to our local state
        setInitialData(outcomeData);

        handleEventChange({
          name: "isOutcomeDataLoaded",
          value: true,
        });
      }
      setPolicyOutcomeData(outcomeData);

      trigger();
    };
    updateLocalState();
  }, [isInitialLoading]); //? If this is a new Policy. Coming directly from the Interview Questions

  useEffect(() => {
    const updateLocalState = async () => {
      if (
        isDataUpdating ||
        newBusinessState == null ||
        retrieveQuoteJourney === false
      )
        return;
      setPolicyOutcomeData(newBusinessState);
    };
    updateLocalState();
  }, [isDataUpdating]); //? If this is an existing Policy. Coming from a Policy retrieval

  const [frequency, setFrequency] = useState("monthly");

  const handleOnChange = (e) => {
    if (!e) return;
    setFrequency(e);
  };

  let showData = !isObjEmpty(policyOutcomeData);
  // const isOnline = dataSet?.application === Application.ONLINE;
  const isOnline = false;

  //----------------------------------------------------------------

  return (
    <>
      <FadeInOut
        show={showData === false}
        duration={500}
        style={{ width: "100%" }}
      >
        <Skeleton
          sx={{ bgcolor: "#F1F9FA" }}
          animation="wave"
          variant="rounded"
          width={"100%"}
          height={"75vh"}
        />
      </FadeInOut>
      {showData === true &&
      policyOutcomeData?.auraStatusDisplay === AURA_OUTCOME.ACC.toString() ? (
        <>
          <SuccessfulOutcome
            fadeIn={showData}
            handleOnChange={handleOnChange}
            currentPage={currentPage}
            frequency={frequency}
            dataSet={dataSet}
            isDirty={isDirty}
            isValid={isValid}
            retrieveQuoteJourney={retrieveQuoteJourney}
            isOnline={isOnline}
            accessToken={accessToken}
            isRetrievePolicyExpired={isRetrievePolicyExpired}
            errors={errors}
            setValue={setValue}
            handleSubmit={handleSubmit}
            handleInputChange={handleInputChange}
            policyOutcomeAcceptTerms={dataSet?.policyOutcomeAcceptTerms}
          />
        </>
      ) : null}

      {showData === true &&
      (policyOutcomeData?.auraStatusDisplay === AURA_OUTCOME.DCL.toString() ||
        policyOutcomeData?.auraStatusDisplay === AURA_OUTCOME.PP.toString() ||
        policyOutcomeData?.auraStatusDisplay === AURA_OUTCOME.RUW.toString())
        ? decline()
        : null}
    </>
  );
}
